// ─── Secure Chat — per-user identity key lifecycle ────────────────────────────
// See CHAT_E2E_DESIGN.md §3. This context is the single place that decides,
// for the signed-in user on THIS device, which of four states applies:
//
//   'checking'     — still looking (local IndexedDB + one Firestore read)
//   'needs-setup'  — no chatKeys/{uid} doc exists anywhere yet: first-ever use
//   'needs-unlock' — a public key exists server-side, but this device has no
//                    local private key (new device, or cleared browser data)
//   'ready'        — privateKey/publicKey are loaded in memory for this tab
//
// components/Chat/ChatKeyGate.js renders the first-run / unlock modals off of
// this context's status; the future message send/receive pipeline (#149) and
// attachment encryption (#150) will read `identity.privateKey` /
// `identity.publicKey` from here rather than re-deriving or re-fetching them.
//
// The decrypted private key lives ONLY in this context's React state (memory,
// this tab, this load) plus the per-device IndexedDB cache in
// crypto/keyStore.js. It is never written anywhere else — see crypto/chat.js
// and firebase/firestore.js's "Secure Chat — identity keys" section for the
// two places real cryptography and real network calls happen, respectively.

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getLocalKeypair, saveLocalKeypair } from '../crypto/keyStore';
import { getChatKey, setChatKey, getChatKeyBackup, setChatKeyBackup } from '../firebase/firestore';
import {
  generateIdentityKeypair,
  wrapPrivateKeyWithPassphrase,
  unwrapPrivateKeyWithPassphrase,
} from '../crypto/chat';

const ChatIdentityContext = createContext(null);

export function ChatIdentityProvider({ children }) {
  const { userProfile } = useAuth();
  const userId = userProfile?.id || null;
  const companyId = userProfile?.companyId || null;

  const [status, setStatus] = useState('checking');
  const [identity, setIdentity] = useState(null); // { publicKey, privateKey }
  const [error, setError] = useState(null);

  // Re-runs the local-IndexedDB-then-server-public-key check whenever the
  // signed-in user (or their company) changes — e.g. on sign-out/sign-in as
  // a different account in the same browser, which must not leak the
  // previous user's loaded key into the new session's state.
  useEffect(() => {
    let cancelled = false;
    setIdentity(null);
    setError(null);

    if (!userId || !companyId) {
      setStatus('checking');
      return;
    }

    setStatus('checking');
    (async () => {
      try {
        const local = await getLocalKeypair(userId);
        if (cancelled) return;
        if (local && local.privateKey && local.publicKey) {
          setIdentity({ publicKey: local.publicKey, privateKey: local.privateKey });
          setStatus('ready');
          return;
        }

        const serverKey = await getChatKey(companyId, userId);
        if (cancelled) return;
        setStatus(serverKey ? 'needs-unlock' : 'needs-setup');
      } catch (err) {
        console.error('[ChatIdentityContext] key check failed', err);
        if (!cancelled) {
          setError(err.message || 'Failed to check chat key status');
          setStatus('needs-setup'); // fail toward the safer/simpler recovery path
        }
      }
    })();

    return () => { cancelled = true; };
  }, [userId, companyId]);

  // First-ever use of chat for this user (status === 'needs-setup'). Generates
  // a fresh keypair, publishes the public half, and stores a passphrase-
  // wrapped backup of the private half server-side per design doc §3.
  const completeSetup = useCallback(async (passphrase) => {
    if (!userId || !companyId) throw new Error('Not signed in');
    const { publicKey, privateKey } = await generateIdentityKeypair();
    await setChatKey(companyId, userId, publicKey);
    const backup = await wrapPrivateKeyWithPassphrase(privateKey, passphrase);
    await setChatKeyBackup(companyId, userId, backup);
    await saveLocalKeypair(userId, { publicKey, privateKey });
    setIdentity({ publicKey, privateKey });
    setStatus('ready');
  }, [userId, companyId]);

  // New device / cleared browser data (status === 'needs-unlock'). Throws on
  // a wrong passphrase — components/Chat/UnlockChatModal.js is expected to
  // catch that and show an inline error, with no server round-trip needed to
  // determine "wrong" vs "right" beyond the one read of the backup blob.
  const completeUnlock = useCallback(async (passphrase) => {
    if (!userId || !companyId) throw new Error('Not signed in');
    const backup = await getChatKeyBackup(companyId, userId);
    if (!backup) throw new Error('No recovery backup found for your account on this company.');
    const privateKey = await unwrapPrivateKeyWithPassphrase(backup, passphrase); // throws on wrong passphrase
    const serverKey = await getChatKey(companyId, userId);
    const publicKey = serverKey?.publicKey;
    if (!publicKey) throw new Error('Could not find your public key — contact an admin.');
    await saveLocalKeypair(userId, { publicKey, privateKey });
    setIdentity({ publicKey, privateKey });
    setStatus('ready');
  }, [userId, companyId]);

  // Re-wraps the already-unlocked private key under a new passphrase and
  // overwrites the server backup. Not wired into any UI yet — exposed here
  // for a future "change my recovery passphrase" Settings action (#148+),
  // since the crypto/firestore plumbing it needs already exists.
  const changePassphrase = useCallback(async (newPassphrase) => {
    if (!identity?.privateKey) throw new Error('Chat is locked — unlock it before changing the passphrase');
    const backup = await wrapPrivateKeyWithPassphrase(identity.privateKey, newPassphrase);
    await setChatKeyBackup(companyId, userId, backup);
  }, [identity, companyId, userId]);

  return (
    <ChatIdentityContext.Provider value={{ status, identity, error, completeSetup, completeUnlock, changePassphrase }}>
      {children}
    </ChatIdentityContext.Provider>
  );
}

export const useChatIdentity = () => useContext(ChatIdentityContext);
